import { Image, message } from 'antd';
import DefaultButton from 'components/button/DefaultButton';
import NoImage from 'components/etc/NoImage';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import isDownload from 'hooks/isDownload';
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import { ICommonCode, IProject } from 'utils/types/storyTypes';
import MinicutRegisterModal from './MinicutRegisterModal';
import SelectMultiple from '../_component/SearchMultiple';
import { IMinicut } from 'utils/types/resourceTypes';
import { handleMessage } from 'utils/helper/message';
import { minicutApi } from 'apis/resource/minicutApis';
import DeleteModal from 'components/modal/DeleteModal';
import { characterApis } from 'apis/resource/characterApis';

import { Edit2, ReceiveSquare, Trash } from 'iconsax-react';
import ActionButton from 'components/table/ActionButton';

/**
 * * 리소스 - 미니컷 조회 테이블
 */

interface IProps {
  selectProject: IProject;
}
const MinicutTable = ({ selectProject }: IProps) => {
  const [refresh, setRefresh] = useState<boolean>(false);
  const { t } = useTranslation();
  const location = useLocation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const { lang } = cookies;

  const [searchData, setSearchData] = useState<string[]>([]);

  const [visible, setVisible] = useState({
    register: false,
    zip: false,
    delete: false,
    minicutName: '',
  }); // 모달 show on/off

  const [minicutData, setMinicutData] = useState<IMinicut[] | null>(null);
  const [tableData, setTableData] = useState<IMinicut[] | null>(null);
  const [charactersData, setCharacterData] = useState<ICommonCode[] | null>(
    null,
  );
  const [selectCharacterData, setSelectCharacterData] = useState<
    ICommonCode[] | null
  >(null);
  useEffect(() => {
    if (charactersData) {
      setSelectCharacterData(charactersData);
    }
  }, [charactersData]);

  const handleFetchData = async () => {
    if (projectId) {
      try {
        const minicutRes = await minicutApi.minicutList(projectId.toString());
        setMinicutData(minicutRes.list);
        const characterRes = await characterApis.list(projectId.toString());
        setCharacterData(characterRes);
      } catch (err) {
        handleMessage(message, 'error', 'Call api error');
      }
    }
  };

  useEffect(() => {
    handleFetchData();
  }, [refresh]);
  useEffect(() => {
    if (minicutData) {
      setTableData(minicutData);
    }
  }, [minicutData]);

  const [selectedId, setSelectedId] = useState<null | string>(null);

  /// 서버 전송
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const handleDelete = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true);
        await minicutApi.minicutDelete(selectedId.toString());
        setRefresh(!refresh);
        setVisible({ ...visible, delete: false });
        setDeleteLoading(false);
      } catch (err) {
        setDeleteLoading(false);
        handleMessage(message, 'error', 'Please try again');
      }
    }
  };

  const handleClickDeleteModal = (record) => {
    setSelectedId(record.id);
    setVisible({
      ...visible,
      delete: true,
      minicutName: record?.image_name,
    });
  };

  // 업로드 모달 열기
  const openUploadMinicut = (e) => {
    const { name } = e?.target;
    setVisible({ ...visible, [name]: true });
  };

  /// 수정 이동
  const openUpdatePage = (record) => {
    navigate(`${record.id}/update`, {
      state: {
        record: record,
      },
    });
  };

  /// 다운로드
  const downloadImage = (record) => {
    // 다운로드 함수로 보내준다.
    isDownload(record);
  };

  // 테이블 우측 액션 메뉴
  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: <ReceiveSquare size='24' onClick={() => downloadImage(record)} />,
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: <Edit2 size='24' onClick={() => openUpdatePage(record)} />,
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 3,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  // 테이블 컬럼
  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '3rem',
      sorter: (a, b) => a.minicut_id - b.minicut_id,
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.preview',
      ).toUpperCase(),
      dataIndex: 'image_url',
      width: '4rem',
      align: 'center',
      render: (text, record) =>
        record.image_url === '' ? (
          <NoImage />
        ) : (
          <Image
            src={record.image_url}
            alt={`${record.image_name}`}
            width={100}
            height={80}
            style={{ objectFit: 'cover', margin: 'auto' }}
          />
        ),
    },
    {
      title: '이미지명',
      dataIndex: 'image_name',
      width: '6rem',
      sorter: (a, b) => {
        if (a.image_name && b.image_name) {
          return a.image_name.localeCompare(b.image_name, undefined, {
            sensitivity: 'base',
          });
        } else if (a.image_name && !b.image_name) {
          return -1;
        } else if (!a.image_name && b.image_name) {
          return 1;
        } else {
          return 0;
        }
      },
    },
    {
      title: '갤러리 노출 이름',
      dataIndex: 'localizations',
      width: '8rem',
      ellipsis: true,
      render: (localizations, record) => {
        const findTitleName = localizations?.find(
          (e) => e.lang === cookies.lang,
        );
        return (
          <>
            {localizations.map((ele) => (
              <p className='content-data'>{findTitleName?.public_name}</p>
            ))}
          </>
        );
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.xOffset',
      ).toUpperCase(),
      dataIndex: 'offset_x',
      align: 'center',
      width: '3rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.yOffset',
      ).toUpperCase(),
      dataIndex: 'offset_y',
      align: 'center',
      width: '3rem',
    },
    {
      title: '연결 캐릭터',
      dataIndex: 'speaker',
      sorter: (a, b) => {
        if (a.speaker && b.speaker) {
          return a.speaker.localeCompare(b.speaker, undefined, {
            sensitivity: 'base',
          });
        } else if (a.speaker && !b.speaker) {
          return -1;
        } else if (!a.speaker && b.speaker) {
          return 1;
        } else {
          return 0;
        }
      },
      width: '3rem',
    },
    {
      title: '크기 배율',
      dataIndex: 'game_scale',
      align: 'center',
      width: '4rem',
    },
    {
      dataIndex: 'move',
      width: '8rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
  ];

  // handle search input onChange
  const handleSelectChange = (value: any, data: any) => {
    setSearchData(value);
  };
  useEffect(() => {
    const cloneData = JSON.parse(JSON.stringify(minicutData));
    if (cloneData) {
      if (searchData.length) {
        const newArray = cloneData.filter((obj) =>
          searchData.includes(obj.speaker),
        );
        setTableData(newArray);
      } else {
        setTableData(minicutData);
      }
    }
  }, [searchData]);

  return (
    <>
      <TableToolbar>
        <SelectMultiple
          optionList={selectCharacterData?.map((e) => {
            return {
              value: e.code,
              label: e.code_name,
            };
          })}
          placeholder='Search...'
          mode='tags'
          onChange={handleSelectChange}
          width='320px'
          value={searchData}
          setValue={setSearchData}
        />
        <div>
          <DefaultButton name='register' onClick={openUploadMinicut}>
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.header.registerMinicut',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      <TableWrapper>
        {tableData && (
          <MainTable
            columns={columnsOrigin}
            dataSource={tableData}
            isPagination={false}
          />
        )}
      </TableWrapper>
      {/* 모달 모음 */}
      <MinicutRegisterModal
        visible={visible.register}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
      />
      <DeleteModal
        visible={visible.delete}
        setVisible={setVisible}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.minicutDeleteModal.title',
        )}
        label={visible.minicutName}
        handleDelete={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

export default MinicutTable;

const TableWrapper = styled.div`
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-image {
    margin: auto;
  }
  .content-data,
  .ant-table-thead > tr > th,
  .ant-table-cell,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .icon {
    cursor: pointer;
    width: 20px;
    height: 20px;
  }
  .icon-delete-outline,
  .icon-edit-outline {
    margin-left: 18px;
  }
`;

const IconArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 5px;
  p {
    font-size: ${theme.fontSizes.normal} !important;
    font-weight: ${theme.fontWeights.bold} !important;
    line-height: 24px !important;
    letter-spacing: 0.2px !important;
    color: ${theme.colors.blackContent} !important;
  }
`;
